import { Directive, ElementRef, Input, Renderer2, effect, signal } from '@angular/core';
import { AccessCacheService } from '../services/access-cache.service';
import { OrgContextService } from '../services/org-context.service';
import { PermissionsService } from '../services/permissions.service';
import { PermissionMatrixStore } from '../services/permission-matrix.store';
import { PermissionKey } from './permission.types';

/**
 * Disables the host element when the current user lacks a permission and applies an optional tooltip.
 * Applies `aria-disabled` and styling hooks when the element cannot be truly disabled.
 */
@Directive({
  selector: '[appDisableIfCannot]',
  standalone: true
})
export class AppDisableIfCannotDirective {
  private permissionKey = signal<PermissionKey | null>(null);
  private resourceId = signal<string | undefined>(undefined);
  private tooltip = signal<string | null>(null);
  private evaluationId = 0;
  private wrapperEl: HTMLElement | null = null;

  constructor(
    private elementRef: ElementRef<HTMLElement>,
    private renderer: Renderer2,
    private permissions: PermissionsService,
    private orgContext: OrgContextService,
    private accessCache: AccessCacheService,
    private matrixStore: PermissionMatrixStore
  ) {
    effect(() => {
      const permission = this.permissionKey();
      const resourceId = this.resourceId();
      const tooltip = this.tooltip();
      const currentEvaluation = ++this.evaluationId;

      this.orgContext.orgRole();
      this.accessCache.version();
      this.matrixStore.version();
      this.matrixStore.currentOrgId();

      if (!permission) {
        this.applyEnabledState(true, null);
        return;
      }

      this.applyEnabledState(false, tooltip);
      this.permissions.canAsync(permission, resourceId).then((can) => {
        if (currentEvaluation !== this.evaluationId) {
          return;
        }
        this.applyEnabledState(can, tooltip);
      }).catch(() => {
        if (currentEvaluation !== this.evaluationId) {
          return;
        }
        this.applyEnabledState(false, tooltip);
      });
    });
  }

  @Input('appDisableIfCannot')
  set appDisableIfCannot(value: PermissionKey) {
    this.permissionKey.set(value);
  }

  @Input()
  set appDisableIfCannotResourceId(value: string | undefined) {
    this.resourceId.set(value);
  }

  @Input()
  set appDisableTooltip(value: string | null) {
    this.tooltip.set(value);
  }

  /** Update the disabled/aria-disabled state and tooltip for the wrapped element. */
  private applyEnabledState(enabled: boolean, tooltip: string | null): void {
    const el = this.elementRef.nativeElement;

    if (enabled) {
      this.renderer.removeAttribute(el, 'disabled');
      this.renderer.removeClass(el, 'app-disabled');
      this.renderer.removeAttribute(el, 'aria-disabled');
      this.renderer.removeStyle(el, 'pointer-events');
      this.unwrapElement();
      return;
    }

    // Disable when possible
    if ('disabled' in el) {
      this.renderer.setProperty(el, 'disabled', true);
    } else {
      this.renderer.setAttribute(el, 'aria-disabled', 'true');
      this.renderer.addClass(el, 'app-disabled');
    }

    // Always suppress pointer events on the element so the wrapper catches hover
    this.renderer.setStyle(el, 'pointer-events', 'none');

    if (tooltip) {
      this.wrapWithTooltip(tooltip);
    }
  }

  /**
   * Wraps the host element in a <span> that carries the tooltip title.
   * Native disabled elements block mouse events, so the wrapper receives
   * hover and shows the tooltip even when the inner element cannot.
   */
  private wrapWithTooltip(tooltip: string): void {
    if (this.wrapperEl) {
      this.renderer.setAttribute(this.wrapperEl, 'title', tooltip);
      return;
    }

    const el = this.elementRef.nativeElement;
    const parent = el.parentNode;
    if (!parent) return;

    const wrapper: HTMLElement = this.renderer.createElement('span');
    this.renderer.setStyle(wrapper, 'display', 'inline-block');
    this.renderer.setStyle(wrapper, 'width', '100%');
    this.renderer.setStyle(wrapper, 'cursor', 'not-allowed');
    this.renderer.setAttribute(wrapper, 'title', tooltip);

    this.renderer.insertBefore(parent, wrapper, el);
    this.renderer.appendChild(wrapper, el);
    this.wrapperEl = wrapper;
  }

  /** Moves the host element back out of the tooltip wrapper and removes it. */
  private unwrapElement(): void {
    if (!this.wrapperEl) return;

    const el = this.elementRef.nativeElement;
    const wrapper = this.wrapperEl;
    const parent = wrapper.parentNode;
    if (parent) {
      this.renderer.insertBefore(parent, el, wrapper);
      this.renderer.removeChild(parent, wrapper);
    }
    this.renderer.removeStyle(el, 'pointer-events');
    this.wrapperEl = null;
  }
}
